<template>
  <div class="detail-page">
    <!-- ── Sub-nav ── -->
    <div class="sub-nav" v-if="(event || loading) && !(isEmbed && checkoutStep === 0)">
      <div class="sub-nav-left">
        <!-- Hidden on the success / minting screens (steps 4 & 5): those have their
             own explicit next-step CTAs, and "back" there used to drop the buyer
             into the card form of an order they had already paid for. -->
        <button class="back-link" v-if="checkoutStep < 4" @click="handleBack">
          <span class="back-arrow">←</span>
          <span v-if="checkoutStep >= 1 && event">{{ event.title }}</span>
          <span v-else>Back</span>
        </button>
      </div>
      <div class="sub-nav-right" v-if="checkoutStep >= 1 && event">
        <span class="sub-nav-meta">{{ formatEventDate(event.eventDate) }}</span>
        <span class="sub-nav-sep">·</span>
        <span class="sub-nav-meta">{{ event.venue }}, Singapore</span>
      </div>
      <button class="share-btn" v-if="checkoutStep === 0 && !isEmbed">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
        Share
      </button>
    </div>

    <!-- ── Progress Bar (steps 1-3) ── -->
    <div class="progress-bar" v-if="checkoutStep >= 1 && checkoutStep <= 3">
      <div class="progress-seg" :class="{ 'progress-seg--active': checkoutStep >= 1 }"></div>
      <div class="progress-seg" :class="{ 'progress-seg--active': checkoutStep >= 2 }"></div>
      <div class="progress-seg" :class="{ 'progress-seg--active': checkoutStep >= 3 }"></div>
    </div>

    <!-- ── Loading ── -->
    <div class="state-center" v-if="loading">
      <p>Loading event…</p>
    </div>

    <!-- ── Error ── -->
    <div class="state-center" v-else-if="error && checkoutStep === 0">
      <p>{{ error }}</p>
    </div>

    <!-- ═══════════════════════════════════════════════════ -->
    <!-- STEP 0 — Event Detail                              -->
    <!-- ═══════════════════════════════════════════════════ -->
    <div v-else-if="checkoutStep === 0 && event" class="page-body">
      <!-- Info + Image card -->
      <div class="detail-card">
        <div class="detail-info">
          <h1 class="detail-title">{{ event.title }}</h1>
          <a v-if="event.sourceDomain" :href="'https://' + event.sourceDomain" class="detail-url" target="_blank">{{ event.sourceDomain }}</a>
          <hr class="info-divider" />
          <div class="info-block">
            <p class="info-label">Event date</p>
            <p class="info-value">{{ formatEventDate(event.eventDate) }}</p>
          </div>
          <div class="info-block">
            <p class="info-label">Location</p>
            <p class="info-value">{{ event.venue }}, Singapore</p>
          </div>
          <hr class="info-divider" />
          <div class="info-block">
            <p class="info-label">Description</p>
            <p class="info-value info-value--desc">{{ event.description }}</p>
          </div>
        </div>
        <div class="detail-img-wrap">
          <img v-if="event.ticketImageUrl" :src="event.ticketImageUrl" class="detail-img" alt="" />
          <div v-else class="detail-img detail-img--placeholder"></div>
        </div>
      </div>

      <!-- Tickets section -->
      <div class="tickets-section">
        <div class="tickets-header">
          <h2 class="tickets-title">
            <span v-if="myEventTickets.length">My Tickets ({{ myEventTickets.length }})</span>
            <span v-else>Tickets</span>
          </h2>
          <div class="tickets-actions-wrap">
            <div class="tickets-actions">
              <button class="btn-outline-pill" v-if="event.seatmapImageUrl" @click="seatmapVisible = !seatmapVisible">View seating</button>
              <button class="btn-gradient-pill" :disabled="!saleOpen || allSoldOut" @click="startCheckout()">
                {{ allSoldOut ? 'Sold out' : saleEnded ? 'Sale ended' : !saleOpen ? 'Not on sale yet' : 'Get tickets' }}
              </button>
            </div>
            <p class="sale-window-note" v-if="saleWindowNote">{{ saleWindowNote }}</p>
          </div>
        </div>

        <div class="tier-cards">
          <div
            class="tier-card"
            v-for="tier in event.tiers"
            :key="tier.id"
            data-testid="tier-card"
          >
            <div class="tier-card__header">
              <h3 class="tier-card__name">{{ tier.name }}</h3>
              <span v-if="tierSoldOut(tier)" class="tier-badge tier-badge--soldout">Sold out</span>
              <span v-else-if="saleEnded" class="tier-badge tier-badge--ended">Sale ended</span>
              <span v-else-if="!saleOpen" class="tier-badge tier-badge--soon">Coming soon</span>
            </div>
            <p class="tier-card__desc" v-if="tier.description">{{ tier.description }}</p>
            <div class="tier-card__footer">
              <span class="tier-card__price">${{ parseFloat(tier.price || 0).toFixed(0) }}</span>
              <button
                class="btn-outline-pill btn-outline-pill--sm"
                :disabled="tierSoldOut(tier) || !saleOpen"
                @click="startCheckout(tier)"
                data-testid="tier-select-btn"
              >{{ tierSoldOut(tier) ? 'Sold out' : saleEnded ? 'Sale ended' : !saleOpen ? 'Not on sale yet' : 'Select' }}</button>
            </div>
          </div>
        </div>

        <!-- My tickets list for this event -->
        <div v-if="myEventTickets.length" class="my-tickets-list">
          <div
            v-for="ticket in myEventTickets"
            :key="ticket.id"
            class="my-ticket-row"
          >
            <div class="my-ticket-row__info">
              <span class="my-ticket-row__tier">{{ ticket.tierName }}</span>
              <span class="my-ticket-row__id">Seat #{{ ticket.id }}</span>
              <span class="my-ticket-row__price" v-if="ticket.tierPrice">${{ parseFloat(ticket.tierPrice).toFixed(0) }}</span>
            </div>
            <button
              class="my-ticket-row__qr-btn"
              :disabled="ticket.status === 'pending'"
              @click.stop="openQr(ticket)"
            >›</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════ -->
    <!-- STEP 1 — Select Tickets                            -->
    <!-- ═══════════════════════════════════════════════════ -->
    <div v-else-if="checkoutStep === 1 && event" class="checkout-layout">
      <!-- Left panel -->
      <div class="checkout-left-wrap">
        <div class="checkout-panel">
          <p>Step 1 of 3</p>
          <h2 class="checkout-heading">Select tickets</h2>
          <div v-for="tier in event.tiers" :key="tier.id">
            <div class="tier-row" :data-testid="`tier-row-${tier.id}`">
              <div class="tier-row__info">
                <div class="tier-row__title-line">
                  <span class="tier-row__name">{{ tier.name }}</span>
                  <span class="tier-row__price">${{ parseFloat(tier.price || 0).toFixed(0) }}</span>
                  <span v-if="tierSoldOut(tier)" class="tier-badge tier-badge--soldout">Sold out</span>
                </div>
                <p class="tier-row__desc" v-if="tier.description">{{ tier.description }}</p>
              </div>
              <div class="tier-row__qty" v-if="cart[tier.id] > 0 && !tierSoldOut(tier)">
                <button class="qty-btn" @click="decrement(tier)" data-testid="qty-minus">−</button>
                <span class="qty-val" data-testid="qty-value">{{ cart[tier.id] }}</span>
                <button class="qty-btn qty-btn--plus" @click="increment(tier)" data-testid="qty-plus">+</button>
              </div>
              <button v-else-if="!tierSoldOut(tier)" class="btn-add" @click="increment(tier)" data-testid="qty-add">
                <span class="btn-add__icon">⊕</span> Add
              </button>
            </div>
            <hr class="tier-divider" v-if="idx < event.tiers.length - 1" />
          </div>
        </div>

        <!-- Footer -->
        <div class="checkout-footer">
          <div class="checkout-total">
            <span class="total-price">${{ totalPrice.toFixed(2) }}</span>
            <span class="total-detail">{{ cartSummaryText }}</span>
          </div>
          <button
            class="btn-gradient-pill"
            :disabled="totalQty === 0"
            @click="goToStep2"
            data-testid="next-1-btn"
          >Continue</button>
        </div>
      </div>

      <!-- Right: Stage Map -->
      <div class="stage-map" v-if="event.seatmapImageUrl">
        <img :src="event.seatmapImageUrl" class="seatmap-img" alt="Venue map" />
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════ -->
    <!-- STEP 2 — Wallet Address                            -->
    <!-- ═══════════════════════════════════════════════════ -->
    <div v-else-if="checkoutStep === 2 && event" class="checkout-layout">
      <!-- Left panel -->
      <div class="checkout-left-wrap">
        <div class="checkout-panel">
          <div class="wallet-header">
            <p>Step 2 of 3</p>
            <h2 class="checkout-heading">
              {{ isGift ? 'Who are these tickets for?' : 'Add a wallet address (optional)' }}
              <button v-if="!isGift" class="info-icon-btn" @click="walletInfoVisible = !walletInfoVisible">ⓘ</button>
            </h2>
            <ol class="wallet-desc" v-if="!isGift">
              <li>You will receive an email with a QR code which can be used as a ticket.</li>
              <li>If you add your wallet address, your ticket will be issued as an NFT and sent to that address.</li>
              <li>If you don't have a wallet yet, you can create one or skip this step.</li>
              <li>If you skip, your NFT will be held with NFTwerks until you transfer the ticket.</li>
            </ol>
            <p class="wallet-desc" v-else>
              We'll email the recipient a link to claim their tickets. They don't need a crypto wallet.
            </p>
          </div>

          <template v-if="!isGift">
            <!-- Wallet Info Popover -->
            <div class="wallet-info-box" v-if="walletInfoVisible">
              <div class="wallet-info-box__header">
                <strong>What's a wallet address?</strong>
                <button class="close-btn" @click="walletInfoVisible = false">✕</button>
              </div>
              <p>A wallet address is a unique identifier for your crypto wallet, similar to an email address but for digital assets. Your NFT tickets will be minted directly to this address.</p>
              <strong>What if I don't have a wallet?</strong>
              <p>You can buy tickets without a wallet and add one later in your profile.</p>
              <button class="btn-gradient-pill btn-gradient-pill--sm" @click="walletInfoVisible = false">Ok</button>
            </div>

            <!-- Wallet Input -->
            <div class="wallet-input-wrap" :class="{ 'wallet-input-wrap--error': walletError }">
              <input
                v-model="walletAddress"
                placeholder="Enter wallet address"
                class="wallet-input"
                data-testid="wallet-input"
                @input="walletError = ''"
              />
            </div>
            <p v-if="walletError" class="wallet-error" data-testid="wallet-error">{{ walletError }}</p>
          </template>
          <button
            v-if="!isGift"
            class="metamask-btn"
            @click="connectMetaMask"
            data-testid="connect-metamask-btn"
          >
            <svg width="18" height="18" viewBox="0 0 318.6 318.6" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0"><path fill="#E2761B" d="M274.1 35.5l-99.7 73.9L193 65.8z"/><path fill="#E4761B" d="M44.4 35.5l98.7 74.6-17.5-44.3zM238.3 206.8l-26.5 40.6 56.7 15.6 16.3-55.3zM33.9 207.7L50.1 263l56.7-15.6-26.5-40.6z"/><path fill="#E4761B" d="M103.6 138.2l-15.8 23.9 56.3 2.5-2-60.5zM214.9 138.2l-39-34.8-1.3 61.2 56.2-2.5zM106.8 247.4l33.8-16.5-29.2-22.8zM177.9 230.9l33.9 16.5-4.7-39.3z"/><path fill="#D7C1B3" d="M211.8 247.4l-33.9-16.5 2.7 22.1-.3 9.3zM106.8 247.4l31.5 14.9-.2-9.3 2.5-22.1z"/><path fill="#233447" d="M138.8 193.5l-28.2-8.3 19.9-9.1zM179.8 193.5l8.3-17.4 20 9.1z"/><path fill="#CD6116" d="M106.8 247.4l4.8-40.6-31.3.9zM207 206.8l4.8 40.6 26.5-39.7zM230.8 162.1l-56.2 2.5 5.2 28.9 8.3-17.4 20 9.1zM110.6 185.2l20-9.1 8.2 17.4 5.3-28.9-56.3-2.5z"/><path fill="#E4751F" d="M87.8 162.1l23.6 46-.8-22.9zM208.1 185.2l-1 22.9 23.7-46zM144.1 164.6l-5.3 28.9 6.6 34.1 1.5-44.9zM174.6 164.6l-2.7 18 1.2 45 6.7-34.1z"/><path fill="#F6851B" d="M179.8 193.5l-6.7 34.1 4.8 3.3 29.2-22.8 1-22.9zM110.6 185.2l.8 22.9 29.2 22.8 4.8-3.3-6.6-34.1z"/><path fill="#C0AD9E" d="M180.3 262.3l.3-9.3-2.5-2.2h-37.7l-2.3 2.2.2 9.3-31.5-14.9 11 9 22.3 15.5h38.3l22.4-15.5 11-9z"/><path fill="#161616" d="M177.9 230.9l-4.8-3.3h-27.5l-4.8 3.3-2.5 22.1 2.3-2.2h37.7l2.5 2.2z"/><path fill="#763D16" d="M278.3 114.2l8.5-40.8-12.7-37.9-96.2 71.4 37 31.3 52.3 15.3 11.6-13.5-5-3.6 8-7.3-6.2-4.8 8-6.1zM31.8 73.4l8.5 40.8-5.4 4 8 6.1-6.1 4.8 8 7.3-5 3.6 11.5 13.5 52.3-15.3 37-31.3L44.4 35.5z"/><path fill="#F6851B" d="M267.2 153.5l-52.3-15.3 15.9 23.9-23.7 46 31.2-.4h46.5zM103.6 138.2l-52.3 15.3-17.4 54.2h46.4l31.1.4-23.6-46zM174.6 164.6l3.3-57.7 15.2-41.1h-67.5l15 41.1 3.5 57.7 1.2 18.2.1 44.8h27.5l.2-44.8z"/></svg>
            Connect MetaMask
          </button>

          <!-- Gift Toggle -->
          <label class="gift-toggle">
            <div class="gift-toggle__checkbox">
              <input type="checkbox" v-model="isGift" data-testid="gift-checkbox" />
              <span class="gift-toggle__label">These tickets are a gift</span>
            </div>
            <div class="gift-arrow">›</div>
          </label>

          <!-- Gift Fields -->
          <div class="gift-fields" v-if="isGift">
            <input
              v-model="giftName"
              placeholder="Recipient’s name"
              class="gift-input"
              data-testid="gift-name"
              @input="giftError = ''"
            />
            <input
              v-model="giftEmail"
              placeholder="Recipient’s email"
              class="gift-input"
              type="email"
              data-testid="gift-email"
              @input="giftError = ''"
            />
            <textarea
              v-model="giftMessage"
              placeholder="Add a personal message (optional)"
              class="gift-input gift-input--message"
              maxlength="500"
              rows="3"
              data-testid="gift-message"
            ></textarea>
            <p v-if="giftError" class="wallet-error" data-testid="gift-error">{{ giftError }}</p>
          </div>

          <!-- Navigation -->
          <div class="step-nav">
            <button class="prev-step-link" @click="checkoutStep = 1">← Previous step</button>
            <button class="btn-gradient-pill" @click="goToStep3" data-testid="next-2-btn">Continue</button>
          </div>
        </div>
      </div>

      <!-- Right: Summary -->
      <div class="summary-panel">
        <h3 class="summary-title">Summary</h3>
        <div class="summary-rows">
          <div class="summary-row" v-for="item in cartItems" :key="item.tierId">
            <span>{{ item.quantity }} x {{ item.name }}</span>
            <span>${{ (item.quantity * parseFloat(item.price || 0)).toFixed(2) }}</span>
          </div>
          <template v-for="fee in event.fees || []">
            <hr class="summary-divider" :key="'fd-' + fee.id" />
            <div class="summary-row" :key="'f-' + fee.id">
              <span>{{ fee.label }}</span>
              <span>${{ feeAmount(fee).toFixed(2) }}</span>
            </div>
          </template>
          <hr class="summary-divider" />
          <div class="summary-row summary-row--total">
            <span>Total</span>
            <span>${{ grandTotal.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════ -->
    <!-- STEP 3 — Payment                                   -->
    <!-- ═══════════════════════════════════════════════════ -->
    <div v-else-if="checkoutStep === 3 && event" class="checkout-layout">
      <!-- Left panel -->
      <div class="checkout-left-wrap">
        <div class="checkout-panel">
          <p>Step 3 of 3</p>
          <h2 class="checkout-heading">Complete your purchase</h2>

          <!-- Stripe Split Card Elements -->
          <div class="stripe-form">
            <div class="stripe-field">
              <label class="field-label">Card number</label>
              <div id="stripe-card-number" class="stripe-input"></div>
            </div>
            <div class="stripe-row">
              <div class="stripe-field">
                <label class="field-label">Expiry date</label>
                <div id="stripe-card-expiry" class="stripe-input"></div>
              </div>
              <div class="stripe-field">
                <label class="field-label">CVC</label>
                <div id="stripe-card-cvc" class="stripe-input"></div>
              </div>
            </div>
            <p v-if="stripeError" class="stripe-error">{{ stripeError }}</p>
          </div>

          <!-- Terms & Conditions — buyer must agree before the order can be placed -->
          <label class="terms-agree">
            <input
              type="checkbox"
              v-model="termsAccepted"
              data-testid="terms-checkbox"
            />
            <span class="terms-agree__label">
              I agree to the
              <a :href="termsUrl" target="_blank" rel="noopener">terms and conditions</a>.
            </span>
          </label>

          <!-- Navigation -->
          <div class="step-nav">
            <button class="prev-step-link" @click="checkoutStep = 2">← Previous step</button>
            <button
              class="btn-gradient-pill"
              @click="handleComplete"
              :disabled="completing || !termsAccepted"
              data-testid="complete-btn"
            >
              {{ completing ? 'Processing…' : (grandTotal > 0 ? 'Pay now' : 'Complete order') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Right: Summary -->
      <div class="summary-panel">
        <h3 class="summary-title">Summary</h3>
        <div class="summary-rows">
          <div class="summary-row" v-for="item in cartItems" :key="item.tierId">
            <span>{{ item.quantity }} x {{ item.name }}</span>
            <span>${{ (item.quantity * parseFloat(item.price || 0)).toFixed(2) }}</span>
          </div>
          <template v-for="fee in event.fees || []">
            <hr class="summary-divider" :key="'fd-' + fee.id" />
            <div class="summary-row" :key="'f-' + fee.id">
              <span>{{ fee.label }}</span>
              <span>${{ feeAmount(fee).toFixed(2) }}</span>
            </div>
          </template>
          <hr class="summary-divider" />
          <div class="summary-row summary-row--total">
            <span>Total</span>
            <span>${{ grandTotal.toFixed(2) }}</span>
          </div>
        </div>
        <div class="summary-note" v-if="walletAddress">
          <p class="summary-note__label">Wallet address:</p>
          <div class="wallet-address-badge">
            <span>{{ shortenAddress(walletAddress) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════ -->
    <!-- STEP 5 — Minting in progress (polling)             -->
    <!-- ═══════════════════════════════════════════════════ -->
    <div v-else-if="checkoutStep === 5" class="checkout-layout">
      <div class="checkout-left-wrap">
        <div class="checkout-panel" style="text-align:center; padding: 48px 32px;">
          <div class="mint-spinner" style="font-size:48px; margin-bottom:24px;">⛓️</div>
          <h2 class="checkout-heading">Preparing your tickets…</h2>
          <p class="success-desc">Payment complete. We’re preparing your tickets. This usually takes 15 to 30 seconds.</p>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════ -->
    <!-- STEP 4 — Success                                   -->
    <!-- ═══════════════════════════════════════════════════ -->
    <div v-else-if="checkoutStep === 4" class="checkout-layout">
      <div class="checkout-left-wrap">
        <!-- Gift purchase confirmation -->
        <div class="checkout-panel" v-if="orderIsGift" data-testid="gift-success">
          <div class="success-progress-bar"></div>
          <h2 class="checkout-heading">Your gift is on its way</h2>
          <p class="success-desc">
            Your gift has been sent to <strong>{{ orderGift.name }}</strong>.
          </p>

          <div class="gift-confirm-box">
            <div class="gift-confirm-row">
              <span class="gift-confirm-row__label">Recipient</span>
              <span class="gift-confirm-row__value">{{ orderGift.name }} &lt;{{ orderGift.email }}&gt;</span>
            </div>
            <div class="gift-confirm-row" v-for="item in cartItems" :key="item.tierId">
              <span class="gift-confirm-row__label">Tickets</span>
              <span class="gift-confirm-row__value">{{ item.quantity }} × {{ item.name }}</span>
            </div>
            <div class="gift-confirm-row" v-if="orderNumber">
              <span class="gift-confirm-row__label">Order no.</span>
              <span class="gift-confirm-row__value">#{{ orderNumber }}</span>
            </div>
            <div class="gift-confirm-row">
              <span class="gift-confirm-row__label">Gift status</span>
              <span class="gift-status-pill" :class="'gift-status-pill--' + giftStatusKey" data-testid="gift-status">
                {{ giftStatusLabel }}
              </span>
            </div>
          </div>

          <ol class="gift-progress">
            <li class="gift-progress__step gift-progress__step--done">Sent</li>
            <li class="gift-progress__step" :class="{ 'gift-progress__step--done': giftStatusKey === 'claimed', 'gift-progress__step--current': giftStatusKey !== 'claimed' }">Waiting to be claimed</li>
            <li class="gift-progress__step" :class="{ 'gift-progress__step--done': giftStatusKey === 'claimed', 'gift-progress__step--current': giftStatusKey === 'claimed' }">Claimed</li>
          </ol>

          <p class="success-desc success-desc--muted">
            We’ll email {{ orderGift.name }} a link to claim the tickets. They don’t need a crypto wallet.
            You can track the status under
            <router-link :to="{ path: '/tickets', query: giftsQuery }" class="link">My Tickets › Gifts</router-link>.
          </p>

          <div class="order-summary-box">
            <h3 class="summary-title">Order Summary</h3>
            <div class="summary-rows">
              <div class="summary-row" v-for="item in cartItems" :key="item.tierId">
                <span>{{ item.name }} x{{ item.quantity }}</span>
                <span>${{ (item.quantity * parseFloat(item.price || 0)).toFixed(2) }}</span>
              </div>
              <hr class="summary-divider" />
              <div class="summary-row summary-row--total">
                <span>Total</span>
                <span>${{ totalPrice.toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Standard purchase confirmation -->
        <div class="checkout-panel" v-else>
          <div class="success-progress-bar"></div>
          <h2 class="checkout-heading">Your order is confirmed</h2>
          <p class="success-desc">
            Check your email for ticket details.
          </p>

          <div class="wallet-section" v-if="walletAddress">
            <p class="wallet-section__line">Tickets will be minted to</p>
            <p class="wallet-section__line">Wallet Address</p>
            <div class="wallet-address-display">
              <span>{{ shortenAddress(walletAddress) }}</span>
            </div>
          </div>

          <div class="order-summary-box">
            <h3 class="summary-title">Order Summary</h3>
            <div class="summary-rows">
              <div class="summary-row" v-for="item in cartItems" :key="item.tierId">
                <span>{{ item.name }} x{{ item.quantity }}</span>
                <span>${{ (item.quantity * parseFloat(item.price || 0)).toFixed(2) }}</span>
              </div>
              <hr class="summary-divider" />
              <div class="summary-row">
                <span>Fees</span>
                <span>$0.00</span>
              </div>
              <hr class="summary-divider" />
              <div class="summary-row summary-row--total">
                <span>Total</span>
                <span>${{ totalPrice.toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Next steps ─────────────────────────────────── -->
        <!-- Previously the confirmation screen was a dead end: no way to view the
             ticket, buy again, get back to the organiser's site, or log out. -->
        <div class="success-actions">
          <div class="success-actions__primary">
            <router-link :to="{ path: '/tickets', query: embedQuery }" class="btn-gradient-pill">View my tickets</router-link>
            <button class="btn-outline-pill" @click="buyMoreTickets">Buy more tickets</button>
          </div>

          <div class="success-actions__links">
            <a v-if="organiserUrl && !isEmbed" :href="organiserUrl" class="success-actions__link" target="_blank" rel="noopener">
              ← Back to {{ organiserLabel }}
            </a>
            <button class="success-actions__link success-actions__link--muted" @click="logoutAndExit">Log out</button>
          </div>
        </div>
      </div>
      <div class="success-illustration">
        <img src="/source/png/checkout-success-illustration.png" alt="" class="success-illustration__img" />
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════ -->
    <!-- STEP -1 — Payment Failed                           -->
    <!-- ═══════════════════════════════════════════════════ -->
    <div v-else-if="checkoutStep === -1" class="checkout-layout">
      <div class="checkout-left-wrap">
        <div class="checkout-panel">
          <h2 class="checkout-heading checkout-heading--fail">Payment unsuccessful</h2>
          <p class="success-desc">Please try again or use another payment method.</p>
          <p v-if="checkoutError" class="checkout-error-detail">{{ checkoutError }}</p>
          <button class="prev-step-link" @click="checkoutStep = 3">← Previous step</button>
        </div>
      </div>
      <div class="summary-panel">
        <h3 class="summary-title">Summary</h3>
        <div class="summary-rows">
          <div class="summary-row" v-for="item in cartItems" :key="item.tierId">
            <span>{{ item.quantity }} x {{ item.name }}</span>
            <span>${{ (item.quantity * parseFloat(item.price || 0)).toFixed(2) }}</span>
          </div>
          <template v-for="fee in event.fees || []">
            <hr class="summary-divider" :key="'fd-' + fee.id" />
            <div class="summary-row" :key="'f-' + fee.id">
              <span>{{ fee.label }}</span>
              <span>${{ feeAmount(fee).toFixed(2) }}</span>
            </div>
          </template>
          <hr class="summary-divider" />
          <div class="summary-row summary-row--total">
            <span>Total</span>
            <span>${{ grandTotal.toFixed(2) }}</span>
          </div>
        </div>
        <div class="summary-note" v-if="walletAddress">
          <div class="wallet-address-badge">
            <span>{{ shortenAddress(walletAddress) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Seatmap Modal -->
    <div class="modal-overlay" v-if="seatmapVisible" @click.self="seatmapVisible = false">
      <div class="seatmap-modal">
        <div class="seatmap-modal__header">
          <span>Seat map</span>
          <button class="close-btn" @click="seatmapVisible = false">✕</button>
        </div>
        <img :src="event.seatmapImageUrl" class="seatmap-modal__img" alt="Seat map" />
      </div>
    </div>

    <!-- Login Modal -->
    <login-modal
      v-if="showLogin"
      :embed="isEmbed"
      @close="showLogin = false"
      @success="onLoginSuccess"
    />

    <!-- QR Modal -->
    <QrModal
      v-if="qrTicket"
      :qr-base64="qrData.qrBase64"
      :expires-at="qrData.expiresAt"
      :ticket-label="qrTicket ? qrTicket.tierName + ' — Ticket #' + qrTicket.id : null"
      @close="qrTicket = null; qrData = {}"
    />
  </div>
</template>

<script>
import ticketingApi from '@/api/ticketing'
import LoginModal from '@/components/events/LoginModal.vue'
import { getCookie } from '@/assets/js/cookie'


export default {
  name: 'EventDetail',
  components: {
    LoginModal,
    QrModal: () => import('@/components/events/QrModal.vue'),
  },
  data() {
    return {
      event: null,
      loading: true,
      error: null,
      checkoutStep: 0,
      cart: {},
      // wallet step
      walletAddress: '',
      walletError: '',
      isGift: false,
      giftName: '',
      giftEmail: '',
      giftMessage: '',
      giftError: '',
      // set on the success screen from the confirmed order
      orderIsGift: false,
      orderNumber: null,
      orderGift: { name: '', email: '' },
      giftStatusKey: 'awaiting_claim',
      walletInfoVisible: false,
      // payment step
      stripe: null,
      // Organizer's connected account `stripe` was initialised for — checkout is a
      // direct charge on that account, so Stripe.js must be scoped to it.
      stripeAccountId: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      stripeError: '',
      completing: false,
      // buyer must tick this before the order can be placed
      termsAccepted: false,
      // modals
      showLogin: false,
      seatmapVisible: false,
      // snapshot of cart for success/fail screens
      _cartSnapshot: [],
      checkoutError: null,
      // async mint polling
      _pollTimer: null,
      // my tickets for this event
      myEventTickets: [],
      qrTicket: null,
      qrData: {},
    }
  },
  computed: {
    isLoggedIn() {
      return !!getCookie('token')
    },
    // ?embed=1 turns this page into a self-contained checkout widget: the global
    // nav/footer are gone (handled in App.vue) and every link that would take the
    // buyer to another part of the app is hidden here.
    isEmbed() {
      const flag = this.$route.query.embed
      return flag === '1' || flag === 'true'
    },
    // Carries the embed flag onto in-app links so the buyer stays chrome-free.
    embedQuery() {
      return this.isEmbed ? { embed: this.$route.query.embed } : {}
    },
    giftsQuery() {
      return Object.assign({ tab: 'gifts' }, this.embedQuery)
    },
    // Terms & Conditions article, opened in a new tab from the payment step so
    // the buyer never loses their in-progress checkout. Same-origin path — works
    // on both staging and production.
    termsUrl() {
      return '/startup/article/detail?articleId=5'
    },
    // Organiser's own website, used for the "Back to …" link on the confirmation
    // screen. `sourceDomain` is a bare host (e.g. "tickets.example.com").
    organiserUrl() {
      const d = this.event && this.event.sourceDomain
      if (!d) return null
      return /^https?:\/\//i.test(d) ? d : `https://${d}`
    },
    organiserLabel() {
      if (!this.event) return 'the event site'
      return this.event.organizerName || this.event.sourceDomain || 'the event site'
    },
    saleOpen() {
      if (!this.event) return false
      const now = Date.now()
      const start = this.event.saleStart ? new Date(this.event.saleStart).getTime() : null
      const end = this.event.saleEnd ? new Date(this.event.saleEnd).getTime() : null
      if (start && now < start) return false
      if (end && now > end) return false
      return true
    },
    saleEnded() {
      if (!this.event) return false
      const end = this.event.saleEnd ? new Date(this.event.saleEnd).getTime() : null
      return end !== null && Date.now() > end
    },
    allSoldOut() {
      if (!this.event) return false
      return this.event.tiers.every(t => this.tierSoldOut(t))
    },
    saleWindowNote() {
      if (!this.event || this.allSoldOut) return ''
      const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      const start = this.event.saleStart ? new Date(this.event.saleStart).getTime() : null
      if (start && Date.now() < start) return `Sales open ${fmt(this.event.saleStart)}`
      if (this.saleEnded) return ''
      if (this.event.saleEnd) return `Sales close ${fmt(this.event.saleEnd)}`
      return ''
    },
    totalQty() {
      return Object.values(this.cart).reduce((s, v) => s + v, 0)
    },
    totalPrice() {
      if (!this.event) return 0
      return this.event.tiers.reduce((sum, tier) => {
        return sum + (this.cart[tier.id] || 0) * parseFloat(tier.price || 0)
      }, 0)
    },
    grandTotal() {
      if (!this.event) return 0
      const fees = (this.event.fees || []).reduce((sum, fee) => sum + this.feeAmount(fee), 0)
      return this.totalPrice + fees
    },
    cartSummaryText() {
      if (!this.event) return ''
      return this.event.tiers
        .filter((t) => this.cart[t.id] > 0)
        .map((t) => `${t.name} x${this.cart[t.id]}`)
        .join(', ')
    },
    cartItems() {
      if (!this.event) return []
      return this.event.tiers
        .filter((t) => this.cart[t.id] > 0)
        .map((t) => ({ tierId: t.id, quantity: this.cart[t.id], name: t.name, price: t.price }))
    },
    giftStatusLabel() {
      return {
        awaiting_claim: 'Sent · awaiting claim',
        claimed: 'Claimed',
      }[this.giftStatusKey] || 'Sent'
    },
  },
  mounted() {
    this.loadEvent()
    // Landing on an event page directly (e.g. from a shared link) previously gave
    // guests no way to log in or sign up until they clicked "Get tickets". Prompt
    // right away so they aren't stuck browsing with no visible path to sign in.
    if (!this.isLoggedIn) {
      this.showLogin = true
    }
  },
  beforeDestroy() {
    this.stopMintPolling()
    this.destroyCardElement()
  },
  watch: {
    checkoutStep(step) {
      if (step === 3) {
        this.$nextTick(() => this.mountCardElement())
      } else {
        this.destroyCardElement()
      }
    },
  },
  methods: {
    async loadEvent() {
      try {
        this.loading = true
        const res = await ticketingApi.getEvent(this.$route.params.id)
        this.event = res.data || res
        const cart = {}
        this.event.tiers.forEach((t) => { cart[t.id] = 0 })
        this.cart = cart
      } catch {
        this.error = 'We couldn’t load this event. Please try again.'
      } finally {
        this.loading = false
      }
      if (this.isLoggedIn) {
        try {
          const res = await ticketingApi.getMyTickets()
          const all = res.data || res
          const eventId = parseInt(this.$route.params.id, 10)
          this.myEventTickets = all.filter((t) => t.eventId === eventId)
        } catch { /* non-fatal */ }
      }
    },
    async openQr(ticket) {
      this.qrTicket = ticket
      this.qrData = {}
      try {
        const res = await ticketingApi.generateQr(ticket.id)
        this.qrData = res.data || res
      } catch {
        this.qrTicket = null
        alert('We couldn’t load your QR code. Please try again.')
      }
    },
    increment(tier) {
      const max = tier.maxPerOrder || 10
      const cur = this.cart[tier.id] || 0
      if (cur < max) this.$set(this.cart, tier.id, cur + 1)
    },
    decrement(tier) {
      const cur = this.cart[tier.id] || 0
      if (cur > 0) this.$set(this.cart, tier.id, cur - 1)
    },
    startCheckout(tier) {
      if (tier) this.$set(this.cart, tier.id, Math.max(1, this.cart[tier.id] || 1))
      if (!this.isLoggedIn) {
        this.showLogin = true
      } else {
        this.checkoutStep = 1
      }
    },
    onLoginSuccess() {
      this.showLogin = false
      this.checkoutStep = 1
    },
    goToStep2() {
      if (this.totalQty === 0) return
      this.checkoutStep = 2
    },
    handleBack() {
      // Payment failed — send them back to the card form to retry, not off the
      // deep end into checkoutStep -2 (which renders nothing).
      if (this.checkoutStep === -1) {
        this.checkoutStep = 3
        return
      }
      // Success / minting — "back" means start a fresh purchase, never re-open
      // the card form of an order that is already paid.
      if (this.checkoutStep >= 4) {
        this.buyMoreTickets()
        return
      }
      if (this.checkoutStep === 0) {
        if (this.isEmbed) return
        this.$router.push('/events')
      } else {
        this.checkoutStep = this.checkoutStep - 1
      }
    },
    // Reset the checkout back to the ticket picker for this event and refresh
    // remaining inventory. Used by "Buy more tickets" and by the sub-nav back
    // arrow on the confirmation screen.
    buyMoreTickets() {
      this.isGift = false
      this.giftName = ''
      this.giftEmail = ''
      this.giftMessage = ''
      this.giftError = ''
      this.walletAddress = ''
      this.walletError = ''
      this.orderIsGift = false
      this.orderNumber = null
      this.orderGift = { name: '', email: '' }
      this.checkoutError = null
      this.stripeError = ''
      this.termsAccepted = false
      this._cartSnapshot = []
      this.stopMintPolling()
      if (this.event) {
        const cart = {}
        this.event.tiers.forEach((t) => { cart[t.id] = 0 })
        this.cart = cart
      }
      this.checkoutStep = 0
      this.$nextTick(() => window.scrollTo(0, 0))
      this.loadEvent()
    },
    logoutAndExit() {
      this.$store.dispatch('logout')
      this.myEventTickets = []
      // The embed widget has no app chrome to land on, so reset it to the
      // ticket picker instead of leaving for /events.
      if (this.isEmbed) {
        this.buyMoreTickets()
        return
      }
      this.$router.push('/events')
    },
    // Stripe.js scoped to the event organizer's connected account. Card Elements
    // and confirmCardPayment must come from the same instance as the account the
    // PaymentIntent was created on, or Stripe can't find the intent.
    ensureStripe() {
      const accountId = this.event && this.event.stripeAccountId
      if (!accountId) return null
      if (this.stripe && this.stripeAccountId === accountId) return this.stripe
      const pk = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
      if (!pk || !window.Stripe) return null
      this.stripe = window.Stripe(pk, { stripeAccount: accountId })
      this.stripeAccountId = accountId
      return this.stripe
    },
    mountCardElement() {
      if (this.cardNumber) return
      if (!this.ensureStripe()) {
        this.stripeError = "This event can't take card payments right now. Please try again later."
        return
      }
      const elementStyle = {
        base: { fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#0a0a0a' },
        invalid: { color: '#dc2626' },
      }
      const elements = this.stripe.elements()
      this.cardNumber = elements.create('cardNumber', { style: elementStyle, showIcon: true })
      this.cardExpiry = elements.create('cardExpiry', { style: elementStyle })
      this.cardCvc = elements.create('cardCvc', { style: elementStyle })
      this.cardNumber.mount('#stripe-card-number')
      this.cardExpiry.mount('#stripe-card-expiry')
      this.cardCvc.mount('#stripe-card-cvc')
      const onError = (e) => { this.stripeError = e.error ? e.error.message : '' }
      this.cardNumber.on('change', onError)
      this.cardExpiry.on('change', onError)
      this.cardCvc.on('change', onError)
    },
    destroyCardElement() {
      ;[this.cardNumber, this.cardExpiry, this.cardCvc].forEach((el) => el && el.destroy())
      this.cardNumber = null
      this.cardExpiry = null
      this.cardCvc = null
      this.stripeError = ''
    },
    async handleComplete() {
      if (!this.termsAccepted) {
        this.stripeError = 'Please agree to the terms and conditions to continue.'
        return
      }
      if (!this.stripe || !this.cardNumber) {
        this.checkoutError = 'Payment is not ready. Please refresh and try again.'
        this.checkoutStep = -1
        return
      }
      this.completing = true
      try {
        const items = this.cartItems.map((i) => ({ tierId: i.tierId, quantity: i.quantity }))
        const gift = this.isGift
          ? {
              recipientName: this.giftName.trim(),
              recipientEmail: this.giftEmail.trim(),
              message: this.giftMessage.trim() || undefined,
            }
          : undefined
        const orderRes = await ticketingApi.createOrder({
          eventId: this.event.id,
          // A gift's tickets are held for the recipient — no buyer wallet is sent.
          walletAddress: this.isGift ? undefined : (this.walletAddress || '').trim() || undefined,
          items,
          gift,
        })
        const { order, clientSecret, stripeAccountId } = orderRes.data || orderRes
        if (stripeAccountId && stripeAccountId !== this.stripeAccountId) {
          // The organizer's Stripe account changed since the page loaded — the card
          // fields belong to the old one and can't confirm this PaymentIntent.
          this.checkoutError = 'Payment details changed for this event. Please refresh the page and try again.'
          this.checkoutStep = -1
          return
        }

        // Snapshot gift details for the success screen before anything can change
        this.orderIsGift = this.isGift
        this.orderNumber = order.id
        if (this.isGift) {
          this.orderGift = { name: this.giftName.trim(), email: this.giftEmail.trim() }
          this.giftStatusKey = 'awaiting_claim'
        }

        const { error } = await this.stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: this.cardNumber },
        })

        if (error) {
          this.checkoutError = error.message || 'Payment failed. Please try again.'
          this.checkoutStep = -1
          return
        }

        this._cartSnapshot = [...this.cartItems]
        this.checkoutStep = 5
        this.startMintPolling(order.id)
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Unknown error'
        const status = err?.response?.status || ''
        console.error('[checkout] failed:', status, msg, err?.response?.data)
        this.checkoutError = `${status ? status + ': ' : ''}${Array.isArray(msg) ? msg.join(', ') : msg}`
        this.checkoutStep = -1
      } finally {
        this.completing = false
      }
    },
    startMintPolling(orderId) {
      const poll = async () => {
        try {
          const orderRes = await ticketingApi.getOrder(orderId)
          const order = orderRes.data || orderRes
          const gift = order.fulfillment && order.fulfillment.gift
          if (gift && gift.isGift) {
            this.orderIsGift = true
            this.giftStatusKey = gift.status || 'awaiting_claim'
            if (gift.recipientName) this.orderGift = { name: gift.recipientName, email: gift.recipientEmail }
          }
          if (order.status === 'confirmed') {
            this.checkoutStep = 4
            return
          }
          if (order.status === 'failed') {
            this.checkoutError = 'We couldn’t prepare your tickets. Please contact support.'
            this.checkoutStep = -1
            return
          }
        } catch (err) {
          console.error('[mint poll] error:', err?.message)
        }
        // still processing — poll again in 4 seconds
        this._pollTimer = setTimeout(poll, 4000)
      }
      this._pollTimer = setTimeout(poll, 4000)
    },
    stopMintPolling() {
      if (this._pollTimer) {
        clearTimeout(this._pollTimer)
        this._pollTimer = null
      }
    },
    isValidEthAddress(addr) {
      return /^0x[0-9a-fA-F]{40}$/.test(addr)
    },
    goToStep3() {
      if (this.isGift) {
        const name = this.giftName.trim()
        const email = this.giftEmail.trim()
        if (!name) {
          this.giftError = 'Enter the recipient’s name.'
          return
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.giftError = 'Enter a valid recipient email address.'
          return
        }
        this.giftName = name
        this.giftEmail = email
        this.giftError = ''
        this.checkoutStep = 3
        return
      }
      const addr = this.walletAddress.trim()
      if (addr && !this.isValidEthAddress(addr)) {
        this.walletError = 'Enter a valid Ethereum wallet address starting with 0x.'
        return
      }
      this.walletAddress = addr  // persist trimmed value
      this.walletError = ''
      this.checkoutStep = 3
    },
    async connectMetaMask() {
      if (!window.ethereum) {
        this.walletError = 'MetaMask is not installed. Please install it to use this feature.'
        return
      }
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' })
        if (accounts && accounts.length > 0) {
          this.walletAddress = accounts[0]
          this.walletError = ''
        }
      } catch (err) {
        if (err.code === 4001) {
          this.walletError = 'Wallet connection cancelled.'
        } else {
          this.walletError = 'We couldn’t connect MetaMask. Please try again.'
        }
      }
    },
    tierSoldOut(tier) {
      return tier.quantitySold >= tier.quantityTotal
    },
    feeAmount(fee) {
      if (fee.type === 'percent') return this.totalPrice * parseFloat(fee.amount)
      return parseFloat(fee.amount)
    },
    shortenAddress(addr) {
      if (!addr) return ''
      if (addr.length <= 12) return addr
      return addr.slice(0, 8) + '…' + addr.slice(-3)
    },
    formatDateRange(start, end) {
      if (!start) return ''
      const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      if (!end) return fmt(start)
      return `${fmt(start)} - ${fmt(end)}`
    },
    formatEventDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleString('en-SG', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped lang="scss">
$bg: #f5f5f7;
$surface: #ffffff;
$text: #0a0a0a;
$muted: #6b7280;
$border: #e5e7eb;
$radius-xl: 24px;
$radius-lg: 20px;
$radius-pill: 50px;
$page-px: 80px;
$font: 'Inter', sans-serif;
$gradient: linear-gradient(90deg, #e879b2 0%, #a855f7 100%);

* { box-sizing: border-box; }

.detail-page {
  font-family: $font;
  background: $bg;
  min-height: 100vh;
  // dvh matches the real visible viewport on mobile (see FrameMain.vue); without
  // it the page reserves a full 100vh inside the shorter #view_main scroll area,
  // pushing the checkout seat map into an unreachable strip below the fold.
  min-height: 100dvh;
  color: $text;
  display: flex;
  flex-direction: column;
}

/* ── Sub-nav ── */
.sub-nav {
  background: $surface;
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px $page-px;
  border-top: 1px solid $border;
}

.sub-nav-left { display: flex; align-items: center; gap: 12px; }
.sub-nav-right { display: flex; align-items: center; gap: 16px; font-size: 14px; color: $muted; }
.sub-nav-sep { color: $muted; }
.sub-nav-meta { font-size: 14px; }

.back-link {
  background: none; border: none; cursor: pointer; font-family: $font;
  display: flex; align-items: center; gap: 10px;
  font-size: 16px; font-weight: 500; color: $text;
  padding: 0;
}
.back-arrow { font-size: 20px; }

.share-btn {
  display: flex; align-items: center; gap: 8px;
  background: $surface; border: 1px solid $border; border-radius: $radius-pill;
  padding: 10px 20px; font-size: 14px; font-weight: 600;
  cursor: pointer; font-family: $font; color: $text;
}

/* ── Progress Bar ── */
.progress-bar {
  display: flex; gap: 4px;
  padding: 0 $page-px;
  background: $surface;
  padding-bottom: 1px;
}
.progress-seg {
  height: 3px; flex: 1; background: $border; border-radius: 2px;
  transition: background 0.3s;
  &--active { background: $gradient; }
}

/* ── Shared buttons ── */
.btn-gradient-pill {
  background: $gradient; color: #fff; border: none;
  border-radius: $radius-pill; padding: 14px 28px;
  font-size: 15px; font-weight: 700; cursor: pointer; font-family: $font;
  white-space: nowrap; transition: opacity 0.2s;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &--sm { padding: 10px 20px; font-size: 13px; }
}

.btn-outline-pill {
  background: $surface; color: $text;
  border: 1.5px solid $text; border-radius: $radius-pill;
  padding: 14px 28px; font-size: 15px; font-weight: 600;
  cursor: pointer; font-family: $font; white-space: nowrap;
  &--sm { padding: 10px 20px; font-size: 13px; }
}

/* ── Page Body (step 0) ── */
.page-body {
  padding: 32px $page-px 80px;
  display: flex; flex-direction: column; gap: 32px;
}

/* ── Detail Card ── */
.detail-card {
  background: $surface; border-radius: $radius-xl;
  display: flex; gap: 40px; padding: 36px 40px;
  align-items: flex-start;
}

.detail-info {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 16px;
}

.detail-title { font-size: 32px; font-weight: 800; margin: 0; line-height: 1.2; }
.detail-url { font-size: 14px; color: $muted; text-decoration: none; margin-top: -8px; }

.info-divider { border: none; border-top: 1px solid $border; margin: 4px 0; }
.info-block { display: flex; flex-direction: column; gap: 4px; }
.info-label { font-size: 14px; font-weight: 700; margin: 0; }
.info-value { font-size: 15px; margin: 0; color: $muted; }
.info-value--desc { line-height: 1.6; }

.detail-img-wrap {
  width: 380px; flex-shrink: 0; border-radius: $radius-lg; overflow: hidden;
  height: 280px;
}
.detail-img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  &--placeholder { background: #d1d5db; }
}

/* ── Tickets Section ── */
.tickets-section {
  display: flex; flex-direction: column; gap: 24px;
}

.tickets-header {
  display: flex; align-items: center; justify-content: space-between;
}
.tickets-title { font-size: 24px; font-weight: 800; margin: 0; }
.tickets-actions-wrap { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.tickets-actions { display: flex; gap: 12px; }
.sale-window-note { margin: 0; font-size: 13px; color: $muted; }

.tier-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.tier-card {
  background: $surface; border-radius: $radius-lg;
  padding: 28px 24px; display: flex; flex-direction: column; gap: 16px;
  &__header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  &__name { font-size: 20px; font-weight: 800; margin: 0; }
  &__desc { font-size: 14px; color: $muted; margin: 0; line-height: 1.6; flex: 1; }
  &__footer {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 8px;
  }
  &__price { font-size: 18px; font-weight: 700; }
}

.tier-badge {
  font-size: 11px; font-weight: 700; border-radius: 50px; padding: 3px 10px;
  &--soldout { background: #fee2e2; color: #dc2626; }
  &--soon { background: #fef9c3; color: #92400e; }
  &--ended { background: #ede9fe; color: #6d28d9; }
}

.tier-row__desc {
  font-size: 13px; color: $muted; margin: 0; line-height: 1.5;
}

.seatmap-img {
  width: 100%; border-radius: $radius-lg; object-fit: contain; display: block;
}

/* ── Checkout layout ── */
.checkout-layout {
  display: flex; gap: 16px;
  padding: 24px $page-px 80px;
  flex: 1; align-items: flex-start;
}

.checkout-left-wrap {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 0;
  position: relative;
}

.checkout-panel {
  background: $surface; border-radius: $radius-xl;
  padding: 32px 28px;
  display: flex; flex-direction: column; gap: 20px;
}

.checkout-heading {
  font-size: 28px; font-weight: 800; margin: 0;
  &--fail { color: #dc2626; }
}

/* ── Tier rows (step 1) ── */
.tier-row {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; padding: 20px 8px;
  &__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 8px; }
  &__title-line { display: flex; align-items: baseline; gap: 10px; }
  &__name { font-size: 18px; font-weight: 700; }
  &__price { font-size: 16px; font-weight: 400; color: $muted; }
  &__perks {
    margin: 0; padding-left: 18px; font-size: 14px;
    line-height: 1.6; color: $muted;
  }
  &__qty {
    display: flex; align-items: center; gap: 12px; flex-shrink: 0; padding-top: 4px;
  }
}

.read-more { font-size: 13px; font-weight: 600; cursor: pointer; }

.qty-btn {
  width: 32px; height: 32px; border-radius: 50%; border: 1.5px solid $border;
  background: $surface; cursor: pointer; font-size: 18px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; color: $text;
  font-family: $font;
  &--plus {
    background: $gradient; border-color: transparent; color: #fff;
  }
}
.qty-val { font-size: 18px; font-weight: 700; min-width: 24px; text-align: center; }

.btn-add {
  display: flex; align-items: center; gap: 6px;
  border: 1.5px solid $border; border-radius: $radius-pill;
  background: $surface; padding: 8px 18px;
  font-size: 14px; font-weight: 600; cursor: pointer; font-family: $font;
  color: $text; flex-shrink: 0;
  &__icon { font-size: 16px; }
}

.tier-divider { border: none; border-top: 1px solid $border; margin: 0; }

/* ── My Tickets list (event detail) ── */
.my-tickets-list {
  margin-top: 16px;
  border-top: 1px solid $border;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.my-ticket-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 12px;
  background: $bg;
  gap: 12px;

  &__info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    flex-wrap: wrap;
  }

  &__tier {
    font-size: 14px;
    font-weight: 700;
  }

  &__id {
    font-size: 13px;
    color: rgba(10,10,10,0.5);
  }

  &__price {
    font-size: 13px;
    font-weight: 600;
    color: rgba(10,10,10,0.7);
  }

  &__qr-btn {
    background: none;
    border: 1px solid rgba(0,0,0,0.15);
    border-radius: 50%;
    width: 32px;
    height: 32px;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: rgba(10,10,10,0.6);

    &:disabled { opacity: 0.35; cursor: not-allowed; }
    &:not(:disabled):hover { background: rgba(0,0,0,0.06); }
  }
}

/* ── Checkout Footer ── */
.checkout-footer {
  background: $surface; border-top: 1px solid $border;
  border-radius: 0 0 $radius-xl $radius-xl;
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 28px; gap: 24px; margin-top: 1px;
}
.checkout-total { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.total-price { font-size: 24px; font-weight: 800; }
.total-detail { font-size: 14px; color: $muted; }

/* ── Stage Map ── */
.stage-map {
  width: 50%; flex-shrink: 0;
  display: flex; flex-direction: column; gap: 8px;
  padding-top: 8px;
}

.sm-zone {
  display: flex; align-items: center; justify-content: center;
  border-radius: 12px; font-size: 13px; font-weight: 700;
  padding: 18px; text-align: center; border: 1.5px solid $border;
  &--header { background: $surface; }
  &--gold { background: #ede9fe; color: #7c3aed; border-color: #c4b5fd; flex: 1; }
  &--silver { background: #f3e8ff; color: #9333ea; border-color: #d8b4fe; }
  &--standing { background: #f9fafb; min-height: 120px; }
  &--neutral { background: $surface; flex: 1; font-size: 12px; }
  &--entry { flex: 0 0 auto; padding: 18px 12px; }
}
.sm-row {
  display: flex; gap: 8px;
  &--bottom { }
}

/* ── Wallet Step ── */
.wallet-header { display: flex; flex-direction: column; gap: 10px; }
.wallet-desc { font-size: 14px; color: $muted; line-height: 1.6; margin: 0; }
ol.wallet-desc { padding-left: 20px; }

.info-icon-btn {
  background: #f3f4f6; border: none; border-radius: 50%;
  width: 22px; height: 22px; font-size: 12px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  vertical-align: middle; margin-left: 6px; font-family: $font;
}

.wallet-info-box {
  background: #faf5ff; border: 1px solid #d8b4fe; border-radius: $radius-lg;
  padding: 20px; display: flex; flex-direction: column; gap: 10px;
  font-size: 14px; line-height: 1.6;
  &__header { display: flex; justify-content: space-between; align-items: center; }
  p { margin: 0; color: $muted; }
  strong { display: block; margin-top: 4px; }
}

.wallet-input-wrap {
  display: flex; align-items: center;
  background: #f3f4f6; border-radius: $radius-pill;
  padding: 14px 20px; gap: 12px;
  border: 1.5px solid transparent; transition: border-color 0.2s;
  &--error { border-color: #dc2626; background: #fff5f5; }
}
.wallet-input {
  flex: 1; background: none; border: none; outline: none;
  font-size: 15px; font-family: $font; color: $text;
  &::placeholder { color: #9ca3af; }
}
.wallet-error {
  font-size: 13px; color: #dc2626; margin: 0; padding-left: 4px;
}
.metamask-btn {
  display: flex; align-items: center; gap: 8px;
  background: #f3f4f6; border: 1.5px solid $border; border-radius: $radius-pill;
  padding: 12px 20px; font-size: 14px; font-weight: 600;
  cursor: pointer; font-family: $font; color: $text;
  width: fit-content; transition: border-color 0.2s;
  &:hover { border-color: #a855f7; }
}
.gift-toggle {
  display: flex; align-items: center; justify-content: space-between;
  background: #f3f4f6; border-radius: $radius-pill; padding: 16px 20px;
  cursor: pointer;
  &__checkbox { display: flex; align-items: center; gap: 12px; font-size: 15px; font-weight: 500; }
  input[type="checkbox"] { width: 18px; height: 18px; cursor: pointer; accent-color: #a855f7; }
}
.gift-arrow { font-size: 20px; color: $muted; }

.gift-fields { display: flex; flex-direction: column; gap: 12px; }
.gift-input {
  width: 100%; background: #f3f4f6; border: none; border-radius: 12px;
  padding: 14px 20px; font-size: 15px; font-family: $font; outline: none;
  &::placeholder { color: #9ca3af; }
}

.step-nav {
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 8px;
}
.prev-step-link {
  background: none; border: none; font-size: 14px; font-weight: 600;
  cursor: pointer; color: $text; font-family: $font;
  display: flex; align-items: center; gap: 4px;
}

/* ── Payment Step ── */
.stripe-form { display: flex; flex-direction: column; gap: 16px; }
.stripe-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.stripe-field { display: flex; flex-direction: column; gap: 6px; }
.stripe-input {
  background: #f3f4f6; border-radius: 10px; padding: 14px 16px;
  border: 1.5px solid transparent; transition: border-color 0.2s;
  &:focus-within { border-color: #a855f7; }
}
.stripe-error { font-size: 13px; color: #dc2626; margin: 0; padding-left: 4px; }
.field-label { font-size: 13px; font-weight: 600; color: $muted; }

.terms-agree {
  display: flex; align-items: flex-start; gap: 10px;
  margin-top: 16px; cursor: pointer;
  input[type="checkbox"] {
    width: 18px; height: 18px; margin-top: 1px; flex-shrink: 0;
    cursor: pointer; accent-color: #a855f7;
  }
  &__label { font-size: 14px; line-height: 1.4; color: $text; }
  a { color: #a855f7; text-decoration: underline; }
}

/* ── Summary Panel ── */
.summary-panel {
  width: 300px; flex-shrink: 0;
  background: $surface; border-radius: $radius-xl;
  padding: 24px 22px; display: flex; flex-direction: column; gap: 16px;
  align-self: flex-start; position: sticky; top: 16px;
}
.summary-title { font-size: 18px; font-weight: 800; margin: 0; }
.summary-rows { display: flex; flex-direction: column; gap: 12px; }
.summary-row {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 14px; color: $muted;
  &--total { font-size: 16px; font-weight: 700; color: $text; }
}
.summary-divider { border: none; border-top: 1px solid $border; margin: 0; }
.summary-note { display: flex; flex-direction: column; gap: 6px; }
.summary-note__label { font-size: 12px; color: $muted; margin: 0; }

.wallet-address-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: #f3f4f6; border-radius: $radius-pill;
  padding: 8px 16px; font-size: 13px; font-weight: 600;
}

/* ── Success ── */
.success-progress-bar {
  height: 6px; border-radius: 3px; background: $gradient;
}
.success-desc { font-size: 15px; color: $muted; line-height: 1.6; margin: 0; }
.checkout-error-detail { font-size: 13px; color: #c0392b; background: #fdecea; border-radius: 8px; padding: 10px 14px; margin-top: 12px; font-family: monospace; word-break: break-all; }
.wallet-section {
  display: flex; flex-direction: column;
}
.wallet-section__line { font-size: 15px; color: $muted; margin: 0; line-height: 1.5; }
.wallet-address-display {
  display: flex; align-items: center; gap: 10px;
  font-size: 20px; font-weight: 700; color: $text; margin-top: 8px;
}
.order-summary-box {
  background: #f9fafb; border-radius: $radius-lg; padding: 20px;
  display: flex; flex-direction: column; gap: 14px;
}

/* ── Confirmation next-step actions ── */
.success-actions {
  display: flex; flex-direction: column; gap: 16px;
  margin-top: 20px;
}
.success-actions__primary {
  display: flex; gap: 12px; flex-wrap: wrap;
  a, button {
    flex: 1 1 auto; min-width: 160px;
    text-align: center; text-decoration: none;
    display: inline-flex; align-items: center; justify-content: center;
  }
}
.success-actions__links {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
}
.success-actions__link {
  background: none; border: none; padding: 0; cursor: pointer;
  font-family: $font; font-size: 14px; font-weight: 600;
  color: #a855f7; text-decoration: none;
  &--muted { color: $muted; }
  &:hover { text-decoration: underline; }
}

/* ── Gift confirmation ── */
.gift-input--message { resize: vertical; min-height: 76px; line-height: 1.5; }

.success-desc--muted { font-size: 13px; margin-top: 4px; }
.success-desc .link,
.success-desc--muted .link { color: #a855f7; font-weight: 600; text-decoration: none; }

.gift-confirm-box {
  background: #f9fafb; border-radius: $radius-lg; padding: 16px 20px;
  display: flex; flex-direction: column; gap: 10px; margin: 16px 0 4px;
}
.gift-confirm-row {
  display: flex; justify-content: space-between; align-items: baseline; gap: 16px;
  font-size: 14px;
  &__label { color: $muted; flex-shrink: 0; }
  &__value { color: $text; font-weight: 600; text-align: right; word-break: break-word; }
}
.gift-status-pill {
  font-size: 12px; font-weight: 700; padding: 3px 12px; border-radius: $radius-pill;
  background: #fff3cd; color: #856404;
  &--claimed { background: #d4edda; color: #155724; }
}

.gift-progress {
  list-style: none; padding: 0; margin: 16px 0;
  display: flex; align-items: center; gap: 8px;
  &__step {
    flex: 1; text-align: center; font-size: 12px; font-weight: 600;
    color: $muted; padding: 8px 4px; border-radius: $radius-pill;
    background: #f3f4f6; position: relative;
    &--current { color: $text; background: #ede9fe; }
    &--done { color: #155724; background: #d4edda; }
  }
}
.success-illustration {
  width: 300px; flex-shrink: 0; display: flex;
  align-items: center; justify-content: center;
}
.success-illustration__img { width: 100%; height: auto; object-fit: contain; }

/* ── States ── */
.state-center {
  display: flex; align-items: center; justify-content: center;
  height: 400px; font-size: 16px; color: $muted;
}

/* ── Close button ── */
.close-btn {
  background: none; border: none; cursor: pointer; font-size: 16px;
  color: $muted; font-family: $font; padding: 0;
}

/* ── Seatmap Modal ── */
.seatmap-modal {
  background: $surface; border-radius: $radius-xl; padding: 28px;
  width: 680px; max-width: calc(100vw - 32px);
  display: flex; flex-direction: column; gap: 16px;
  &__header {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 18px; font-weight: 700;
  }
  &__img {
    width: 100%; border-radius: $radius-lg; object-fit: contain; display: block;
    max-height: 70vh;
  }
}

/* ── Modal overlay (seatmap) ── */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center; z-index: 300;
}

/* ── Responsive ── */
@media (max-width: 1080px) {
  .sub-nav, .progress-bar, .page-body { padding-left: 24px; padding-right: 24px; }
  .checkout-layout { flex-direction: column; padding-left: 24px; padding-right: 24px; }
  .stage-map, .summary-panel, .success-illustration { width: 100%; }
  .tier-cards { grid-template-columns: 1fr; }
  .detail-card { flex-direction: column; }
  .detail-img-wrap { width: 100%; }
}

@media (max-width: 640px) {
  .sub-nav {
    padding-left: 16px; padding-right: 16px;
    flex-wrap: wrap; gap: 8px;
  }
  .sub-nav-right { display: none; }
  .back-link { font-size: 14px; }
  .back-link span:not(.back-arrow) {
    max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  .tickets-header {
    flex-direction: column; align-items: flex-start; gap: 12px;
  }
  .tickets-actions-wrap { width: 100%; align-items: flex-start; }
  .tickets-actions { width: 100%; justify-content: flex-start; }

  .page-body { padding-left: 16px; padding-right: 16px; }
  .checkout-layout { padding-left: 16px; padding-right: 16px; }
  .progress-bar { padding-left: 16px; padding-right: 16px; }

  .checkout-panel { padding: 20px 16px; }
  .checkout-footer { padding: 16px 16px; }

  .form-row--2 { flex-direction: column; }
  .add-card-modal { width: calc(100vw - 32px); }
}
</style>
